import React, { useEffect, useState } from "react";
import { Smartphone, Download, MonitorSmartphone, CheckCircle2, Info, Loader2 } from "lucide-react";
import { useBranding } from "../hooks/useBranding";

const APK_PATH = "/downloads/terabit-network.apk";

export const DownloadApkPage = () => {
  const branding = useBranding();
  const [apkReady, setApkReady] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    fetch(APK_PATH, { method: "HEAD" })
      .then((r) => setApkReady(r.ok && Number(r.headers.get("content-length") || 0) > 100000))
      .catch(() => setApkReady(false));
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstallPwa = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="download-apk-page">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2.5">
          <Smartphone className="w-6 h-6 text-teal-400" /> Download Aplikasi Android
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Pasang {branding.app_name || "aplikasi billing"} di perangkat Android Anda — via file APK atau instal langsung dari browser.
        </p>
      </div>

      {/* Kartu utama */}
      <div className="p-6 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl flex flex-col sm:flex-row items-center gap-6">
        <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-teal-500/30 to-cyan-600/20 border border-teal-500/40 flex items-center justify-center flex-shrink-0">
          {branding.app_logo ? (
            <img src={branding.app_logo} alt="logo" className="w-16 h-16 object-contain" />
          ) : (
            <Smartphone className="w-11 h-11 text-teal-300" />
          )}
        </div>
        <div className="flex-1 text-center sm:text-left">
          <h2 className="text-lg font-bold text-white">{branding.app_name || "ISP Billing"}</h2>
          <p className="text-xs text-slate-400 mt-0.5">Versi 1.0 (Android 8.0 ke atas)</p>

          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            {apkReady ? (
              <a
                href={APK_PATH}
                download
                data-testid="download-apk-btn"
                className="px-5 py-3 rounded-xl bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 text-white text-sm font-bold flex items-center justify-center gap-2 shadow-lg shadow-teal-500/25 transition-all"
              >
                <Download className="w-4 h-4" /> Download APK
              </a>
            ) : (
              <button
                disabled
                data-testid="download-apk-btn"
                className="px-5 py-3 rounded-xl bg-slate-800 border border-slate-700 text-slate-500 text-sm font-bold flex items-center justify-center gap-2 cursor-not-allowed"
              >
                {apkReady === null ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {apkReady === null ? "Memeriksa APK..." : "APK sedang disiapkan"}
              </button>
            )}
            {deferredPrompt && (
              <button
                onClick={handleInstallPwa}
                data-testid="install-pwa-btn"
                className="px-5 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 border border-teal-500/40 text-teal-300 text-sm font-bold flex items-center justify-center gap-2 transition-colors"
              >
                <MonitorSmartphone className="w-4 h-4" /> Install Langsung (Tanpa APK)
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Petunjuk instalasi APK */}
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
          <Info className="w-4 h-4 text-teal-400" /> Cara Instal File APK
        </h3>
        <ol className="space-y-2 text-xs text-slate-300 list-none">
          {[
            "Buka halaman ini di browser HP Android Anda, lalu tekan tombol Download APK.",
            "Jika muncul peringatan keamanan, pilih \"Tetap unduh\".",
            "Buka file hasil unduhan, lalu izinkan \"Instal dari sumber tidak dikenal\" bila diminta.",
            "Tekan Instal — ikon aplikasi akan muncul di layar utama seperti aplikasi lainnya.",
          ].map((step, i) => (
            <li key={i} className="flex items-start gap-2.5" data-testid={`apk-step-${i}`}>
              <span className="w-5 h-5 rounded-full bg-teal-500/20 border border-teal-500/40 text-teal-300 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Petunjuk PWA */}
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
          <MonitorSmartphone className="w-4 h-4 text-cyan-400" /> Alternatif: Install dari Browser (PWA)
        </h3>
        <p className="text-xs text-slate-400 mb-3">
          Aplikasi web ini sudah mendukung instalasi langsung tanpa file APK — tampil dan berfungsi seperti aplikasi native.
        </p>
        <ol className="space-y-2 text-xs text-slate-300">
          {[
            "Buka aplikasi ini di Chrome Android.",
            "Tekan menu ⋮ (tiga titik) di kanan atas.",
            "Pilih \"Install app\" atau \"Add to Home screen\".",
            "Ikon aplikasi akan terpasang di layar utama.",
          ].map((step, i) => (
            <li key={i} className="flex items-start gap-2.5" data-testid={`pwa-step-${i}`}>
              <CheckCircle2 className="w-4 h-4 text-cyan-500 flex-shrink-0 mt-0.5" />
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};
